import { currencySymbol } from "../../helper/commonHelper";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import moment from "moment";
import { Modal } from "react-bootstrap";
import DatePicker, { DateObject } from "react-multi-date-picker";
import Select from "react-select";
import { countriesService } from "../../services/countries.service";
import ContentLoader from "../../components/loader/ContentLoader";
import { bookingsService } from "../../services/bookings.service";
import { SOMETHING_WENT_WRONG } from "../../constants/strings";
import {
  Avatar, Badge, DETAIL_ACCENTS, DetailBlock, DetailGroup, EmptyState as Empty, HeroChip, Pill, RouteHero, StatStrip,
  detailTableWrap, detailTdStyle as tdStyle, detailThStyle as thStyle, isEmpty, show, titleCase,
} from "../../components/common/DetailUI";

const timeText = (t) => (t ? String(t).slice(0, 5) : "-");
const dateText = (d) => (d ? moment(d).format("DD MMM YYYY") : "-");
const dateTimeText = (d) => (d ? moment(d).format("DD MMM YYYY, hh:mm A") : "-");

// one accent per section so the page reads as colour-coded blocks
const ACCENTS = {
  summary: DETAIL_ACCENTS.indigo,
  parties: DETAIL_ACCENTS.violet,
  journey: DETAIL_ACCENTS.sky,
  itinerary: DETAIL_ACCENTS.teal,
  fare: DETAIL_ACCENTS.green,
  passengers: DETAIL_ACCENTS.pink,
  timeline: DETAIL_ACCENTS.amber,
};

const PAX_ACCENTS = { adult: DETAIL_ACCENTS.indigo, child: DETAIL_ACCENTS.sky, infant: DETAIL_ACCENTS.pink };

const TITLES = ["Mr", "Mrs", "Ms", "Master", "Miss"];
const GENDER_BY_TITLE = { Mr: "male", Master: "male", Mrs: "female", Ms: "female", Miss: "female" };
const toDateObject = (v) => (v ? new DateObject({ date: v, format: "YYYY-MM-DD" }) : null);

const countrySelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 31,
    borderRadius: 6,
    borderColor: state.isFocused ? "#86b7fe" : "#dee2e6",
    boxShadow: "none",
    fontSize: 14,
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: 29 }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

const BookingDetails = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [editPax, setEditPax] = useState(null); // passenger being edited (form values)
  const [savingPax, setSavingPax] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);

  useEffect(() => {
    loadBooking();
  }, [id]);

  const loadBooking = async () => {
    try {
      setLoading(true);
      const res = await bookingsService.details(id);
      if (res?.success) {
        setBooking(res.resData);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      withReactContent(Swal).fire({ title: "Error", text: SOMETHING_WENT_WRONG, icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  const nullable = (v) => (v === undefined || v === null || String(v).trim() === "" ? null : String(v).trim());

  const openEditPassenger = (p) =>
    setEditPax({
      id: p.id,
      pax_type: String(p.pax_type || "adult").toLowerCase(),
      title: p.title || "",
      first_name: p.first_name || "",
      last_name: p.last_name || "",
      gender: p.gender || "",
      dob: p.dob ? String(p.dob).slice(0, 10) : "",
      wheelchair: p.wheelchair_required === true || String(p.wheelchair_required).toLowerCase() === "yes" ? "Yes" : "No",
      nationality: p.nationality || "",
      passport_number: p.passport_number || "",
      passport_issue_date: p.passport_issue_date ? String(p.passport_issue_date).slice(0, 10) : "",
      passport_expiry: p.passport_expiry ? String(p.passport_expiry).slice(0, 10) : "",
      passport_issuing_country: p.passport_issuing_country || "",
      ticket_number: p.ticket_number || "",
      pnr: p.pnr || "",
      seat_number: p.seat_number || "",
    });

  // country dropdown for the passport issuing country (countries > list), loaded once when the popup first opens
  useEffect(() => {
    if (!editPax || countryOptions.length) return;
    (async () => {
      try {
        const res = await countriesService.list({ page: 1, status: "active" });
        setCountryOptions((res?.resData?.data || []).map((c) => ({ value: c.code, label: c.name })));
      } catch (err) {
        setCountryOptions([]);
      }
    })();
  }, [editPax, countryOptions.length]);

  // choosing a title also picks the matching gender (it can still be changed afterwards)
  const handleTitleChange = (e) => {
    const title = e.target.value;
    setEditPax((cur) => ({ ...cur, title, gender: GENDER_BY_TITLE[title] || cur.gender }));
  };

  const handlePaxChange = (e) => {
    const { name, value } = e.target;
    setEditPax((cur) => ({ ...cur, [name]: value }));
  };

  const handleSavePassenger = async (e) => {
    e.preventDefault();
    if (!editPax.pax_type || !editPax.title || !editPax.gender || !editPax.first_name.trim() || !editPax.last_name.trim()) {
      return toast.error("Type, title, gender, first name and last name are required.");
    }
    if (editPax.passport_issue_date && editPax.passport_expiry && editPax.passport_expiry < editPax.passport_issue_date) {
      return toast.error("Passport expiry must be after the issue date.");
    }
    const confirm = await withReactContent(Swal).fire({
      title: "Update passenger?",
      text: `Save changes for ${editPax.first_name.trim()} ${editPax.last_name.trim()}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, update",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    const passengerPayload = {
      passenger_id: editPax.id,
      pax_type: editPax.pax_type,
      title: nullable(editPax.title),
      first_name: editPax.first_name.trim(),
      last_name: editPax.last_name.trim(),
      gender: nullable(editPax.gender),
      dob: nullable(editPax.dob),
      wheelchair_required: editPax.wheelchair === "Yes",
      nationality: nullable(editPax.nationality),
      passport_number: nullable(editPax.passport_number),
      passport_issue_date: nullable(editPax.passport_issue_date),
      passport_expiry: nullable(editPax.passport_expiry),
      passport_issuing_country: nullable(editPax.passport_issuing_country),
      ticket_number: nullable(editPax.ticket_number),
      pnr: nullable(editPax.pnr),
      seat_number: nullable(editPax.seat_number),
    };

    const payload = {passengers: [passengerPayload]};

    try {
      setSavingPax(true);
      const res = await bookingsService.updatePassenger(booking.id ?? id, editPax.id, payload);
      if (res?.success) {
        toast.success(res.message || "Passenger updated.");
        // show the saved values straight away (the fields live on passengerPayload, not on the { passengers: [] } request body)
        const { passenger_id, ...savedFields } = passengerPayload;
        setBooking((cur) => ({
          ...cur,
          passengers: (cur.passengers || []).map((p) =>
            String(p.id) === String(editPax.id) ? { ...p, ...savedFields, ...(res.resData?.id ? res.resData : {}) } : p,
          ),
        }));
        setEditPax(null);
        // then re-sync with the server without the full-page loader
        bookingsService.details(id).then((fresh) => {
          if (fresh?.success && fresh.resData) setBooking(fresh.resData);
        });
      } else {
        toast.error(res?.message || "Could not update the passenger.");
      }
    } catch (err) {
      withReactContent(Swal).fire({ title: "Error", text: SOMETHING_WENT_WRONG, icon: "error" });
    } finally {
      setSavingPax(false);
    }
  };

  const header = (
    <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-3">
      <div>
        <h4 className="m-0 fw-bold" style={{ color: "var(--page-heading)", fontSize: 20 }}>Booking Details</h4>
        <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#475569" }}>
          <Link to="/dashboard" style={{ color: "#475569" }}><i className="bi bi-house-door"></i></Link>
          <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
          <Link to="/booking-details" style={{ color: "#475569" }}>Bookings</Link>
          <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
          <span className="fw-semibold" style={{ color: "#4a5568" }}>{booking?.booking_ref_id || "Booking Details"}</span>
        </nav>
      </div>
      <Link
        to="/booking-details"
        className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
        style={{ background: "var(--btn-bg)", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}
      >
        <i className="bi bi-list-ul"></i> Booking List
      </Link>
    </div>
  );

  if (loading) return <ContentLoader loaderType="circleSpin" />;

  if (!booking) {
    return (
      <section className="section dashboard">
        {header}
        <Empty text="Booking not found" />
      </section>
    );
  }

  const fare = booking.full_fare_desc;
  const origin = fare?.origin;
  const destination = fare?.destination;
  const stops = fare?.stops || [];
  const passengers = booking.passengers || [];

  const symbol = currencySymbol(booking.currency_code);
  const money = (v) => (isEmpty(v) ? "-" : `${symbol} ${Number(v).toLocaleString("en-IN")}`);
  const paxCount = Number(booking.adult_count || 0) + Number(booking.child_count || 0) + Number(booking.infant_count || 0);
  const flightStops = fare?.flight_stops ?? stops.length;
  const airlineText = fare?.airline ? `${fare.airline.airline_name} (${fare.airline.airline_code})` : null;

  // booking progress: each step is reached when its timestamp is set
  const isCancelled = !!booking.cancelled_at || String(booking.booking_status).toLowerCase() == "cancelled";
  const steps = [
    { label: "Created", icon: "bi-plus-circle", at: booking.created_at },
    { label: "Confirmed", icon: "bi-check2-circle", at: booking.confirmed_at },
    { label: "Ticketed", icon: "bi-ticket-perforated", at: booking.ticketed_at },
    isCancelled
      ? { label: "Cancelled", icon: "bi-x-circle", at: booking.cancelled_at, danger: true }
      : { label: "Completed", icon: "bi-flag", at: booking.completed_at },
  ];

  return (
    <section className="section dashboard">
      <style>{`
        .bk-edit-btn { display:inline-flex; align-items:center; gap:6px; padding:4px 12px; font-size:12px; font-weight:600; border:1px solid #cbd5e1 !important; border-radius:8px; background:#fff !important; color:var(--btn-bg) !important; box-shadow:none !important; }
        .bk-edit-btn:hover { background:#f0fdfa !important; border-color:var(--btn-bg) !important; }
      `}</style>
      {header}

      <RouteHero
        chips={
          <>
            <HeroChip icon="bi-ticket-perforated">{booking.booking_ref_id}</HeroChip>
            {booking.pnr && <HeroChip icon="bi-upc-scan">PNR {booking.pnr}</HeroChip>}
            {(airlineText || fare?.flight_number) && (
              <HeroChip icon="bi-airplane">{[airlineText, fare?.flight_number].filter(Boolean).join(" · ")}</HeroChip>
            )}
          </>
        }
        right={
          <>
            <Badge value={booking.booking_status} />
            <Badge value={booking.payment_status} />
          </>
        }
        from={{
          code: origin?.airport_code || booking.origin,
          city: origin?.city_name,
          time: timeText(origin?.time),
          sub: `${dateText(origin?.date || booking.departure_date)} · T${show(origin?.terminal)}`,
        }}
        to={{
          code: destination?.airport_code || booking.destination,
          city: destination?.city_name,
          time: timeText(destination?.time),
          sub: `${dateText(destination?.date || booking.arrival_date)} · T${show(destination?.terminal)}`,
        }}
        middle={[
          flightStops == 0 ? "Non-stop" : `${flightStops} stop${flightStops > 1 ? "s" : ""}`,
          isEmpty(booking.cabin_class) ? null : titleCase(booking.cabin_class),
        ].filter(Boolean).join(" · ")}
      />

      <StatStrip
        stats={[
          { icon: "bi-wallet2", label: "Total Fare", value: money(booking.total_fare), accent: DETAIL_ACCENTS.green },
          { icon: "bi-cash-coin", label: "Net Fare", value: money(booking.net_fare), accent: DETAIL_ACCENTS.sky },
          { icon: "bi-people-fill", label: "Passengers", value: paxCount, accent: DETAIL_ACCENTS.pink },
          { icon: "bi-calendar-event", label: "Departure", value: dateText(booking.departure_date), accent: DETAIL_ACCENTS.amber },
        ]}
      />

      {/* Booking, parties and journey side by side */}
      <div className="row g-3 mb-3">
        <div className="col-12 col-lg-4">
          <DetailGroup
            icon="bi-ticket-perforated-fill"
            title="Booking"
            accent={ACCENTS.summary}
            rows={[
              { label: "Booking Ref", value: booking.booking_ref_id },
              { label: "PNR", value: booking.pnr },
              { label: "Booking Status", value: booking.booking_status, badge: true },
              { label: "Payment Status", value: booking.payment_status, badge: true },
              { label: "Call Type", value: isEmpty(booking.call_type) ? null : titleCase(booking.call_type) },
              { label: "Created At", value: dateTimeText(booking.created_at) },
            ]}
          />
        </div>
        <div className="col-12 col-lg-4">
          <DetailGroup
            icon="bi-building-fill"
            title="Supplier & Agent"
            accent={ACCENTS.parties}
            rows={[
              { label: "Supplier", value: booking.supplier?.agency_name },
              { label: "Supplier Code", value: booking.supplier_code },
              { label: "Buyer Supplier Code", value: booking.buyer_supplier_code },
              { label: "Agent Code", value: booking.agent_code },
              { label: "Vendor Code", value: booking.vendor_code },
              { label: "Inventory Code", value: booking.inventory_code },
            ]}
          />
        </div>
        <div className="col-12 col-lg-4">
          <DetailGroup
            icon="bi-map-fill"
            title="Journey"
            accent={ACCENTS.journey}
            rows={[
              { label: "Route", value: `${show(booking.origin)} → ${show(booking.destination)}` },
              { label: "Trip Type", value: isEmpty(booking.trip_type) ? null : titleCase(booking.trip_type) },
              { label: "Route Type", value: isEmpty(booking.route_type) ? null : titleCase(booking.route_type) },
              { label: "Departure", value: dateText(booking.departure_date) },
              { label: "Arrival", value: dateText(booking.arrival_date) },
              { label: "Cabin Class", value: isEmpty(booking.cabin_class) ? null : titleCase(booking.cabin_class) },
              {
                label: "Travellers",
                value: `${show(booking.adult_count)} Adult · ${show(booking.child_count)} Child · ${show(booking.infant_count)} Infant`,
              },
            ]}
          />
        </div>
      </div>

      {/* Flight itinerary and fare summary side by side */}
      <div className="row g-3 mb-3">
        {fare && (
          <div className="col-12 col-lg-6">
            <DetailGroup
              icon="bi-airplane-fill"
              title="Flight Itinerary"
              accent={ACCENTS.itinerary}
              rows={[
                { label: "Airline", value: airlineText },
                { label: "Flight Number", value: fare.flight_number },
                {
                  label: "Departs",
                  value: origin ? `${show(origin.airport_code)} · ${dateText(origin.date)} ${timeText(origin.time)} · T${show(origin.terminal)}` : null,
                },
                {
                  label: "Arrives",
                  value: destination ? `${show(destination.airport_code)} · ${dateText(destination.date)} ${timeText(destination.time)} · T${show(destination.terminal)}` : null,
                },
                { label: "Stops", value: flightStops == 0 ? "Non-stop" : flightStops },
                { label: "RBD / Fare Code", value: [fare.rbd, fare.fare_code].filter((v) => !isEmpty(v)).join(" / ") || null },
                { label: "Fare Reference", value: fare.fare_reference_code },
                { label: "Refundable", value: fare.is_refundable === undefined ? null : fare.is_refundable ? "Yes" : "No" },
                { label: "Baggage Policy", value: fare.baggage_policy },
                { label: "Supplier Type", value: isEmpty(fare.supplier_type) ? null : titleCase(fare.supplier_type) },
                { label: "Fare Rule", value: fare.fare_rule==='non_refundable_non_changable' ? "Non-refundable / Non-changeable" : fare.fare_rule, hideIfEmpty: true },
              ]}
            />
          </div>
        )}
        <div className={fare ? "col-12 col-lg-6" : "col-12"}>
          <DetailGroup
            icon="bi-cash-stack"
            title={`Fare Details (${booking.currency_code || "INR"})`}
            accent={ACCENTS.fare}
            rows={[
              { label: "Base Fare", value: money(booking.base_fare) },
              { label: "Tax Amount", value: money(booking.tax_amount) },
              { label: "Airline Fees", value: money(booking.airline_fees) },
              { label: "Other Charges", value: money(booking.other_charges) },
              { label: "Discount", value: money(booking.discount) },
              { label: "Net Fare", value: money(booking.net_fare) },
              { label: "Total Fare", value: money(booking.total_fare), color: "#047857" },
            ]}
          />
        </div>
      </div>

      {fare && stops.length > 0 && (
        <DetailBlock icon="bi-signpost-split" title="Flight Segments" count={stops.length} accent={ACCENTS.itinerary}>
          <div className="table-responsive" style={detailTableWrap}>
            <table className="table table-sm align-middle mb-0" style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  {["Segment", "From", "Dep Time", "Dep Terminal", "To", "Arr Time", "Arr Terminal"].map((h) => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stops.map((st, i) => (
                  <tr key={i}>
                    <td style={tdStyle}><Pill value={Number(st.stop_number) + 1} accent={ACCENTS.itinerary} /></td>
                    <td style={tdStyle} className="fw-bold">{show(st.departure_airport_code)}</td>
                    <td style={tdStyle}>{timeText(st.departure_time)}</td>
                    <td style={tdStyle}>{show(st.departure_terminal)}</td>
                    <td style={tdStyle} className="fw-bold">{show(st.arrival_airport_code)}</td>
                    <td style={tdStyle}>{timeText(st.arrival_time)}</td>
                    <td style={tdStyle}>{show(st.arrival_terminal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DetailBlock>
      )}

      <DetailBlock icon="bi-people-fill" title="Passengers" count={passengers.length} accent={ACCENTS.passengers}>
        {passengers.length > 0 ? (
          <div className="table-responsive" style={detailTableWrap}>
            <table className="table table-sm align-middle mb-0" style={{ fontSize: 13 }}>
              <thead>
                {/* <tr>
                  {["#", "Name", "Type", "Gender", "DOB", "Nationality", "Passport No.", "Passport Issue", "Passport Expiry", "Issuing Country", "Ticket No.", "Seat", "PNR", "Action"].map((h) => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr> */}
                <tr>{["#","Name","Type","Gender","DOB","Nationality","Passport No.","Passport Issue","Passport Expiry","Issuing Country","Ticket No.","Seat","PNR",...(booking.booking_status?.toLowerCase() !== "confirmed" ? ["Action"] : []),
                    ].map((h) => (
                      <th key={h} style={thStyle}>
                        {h}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {passengers.map((p, i) => {
                  const type = String(p.pax_type || "").toLowerCase();
                  const name = [p.title, p.first_name, p.last_name].filter(Boolean).join(" ");
                  return (
                    <tr key={p.id ?? i}>
                      <td style={{ ...tdStyle, color: "#94a3b8" }}>{i + 1}</td>
                      <td style={tdStyle}>
                        <div className="d-flex align-items-center gap-2">
                          {/* <Avatar name={name} accent={PAX_ACCENTS[type] || ACCENTS.passengers} /> */}
                          <span className="fw-semibold text-capitalize">{show(name)}</span>
                        </div>
                      </td>
                      <td style={tdStyle}><Pill value={titleCase(p.pax_type)} accent={PAX_ACCENTS[type] || ACCENTS.passengers} /></td>
                      <td style={tdStyle} className="text-capitalize">{show(p.gender)}</td>
                      <td style={tdStyle}>{dateText(p.dob)}</td>
                      <td style={tdStyle}>{show(p.nationality)}</td>
                      <td style={tdStyle}>{show(p.passport_number)}</td>
                      <td style={tdStyle}>{dateText(p.passport_issue_date)}</td>
                      <td style={tdStyle}>{dateText(p.passport_expiry)}</td>
                      <td style={tdStyle}>{show(p.passport_issuing_country)}</td>
                      <td style={{ ...tdStyle, color: p.ticket_number ? "#1e293b" : "#cbd5e1" }}>{show(p.ticket_number)}</td>
                      <td style={tdStyle}>{show(p.seat_number)}</td>
                      <td style={tdStyle} className="fw-semibold">{show(p.pnr)}</td>
                     
                      {booking.booking_status?.toLowerCase() !== "confirmed" && (
   <td style={tdStyle}><button
    type="button"
    className="bk-edit-btn"
    onClick={() => openEditPassenger(p)}
  >
    <i className="bi bi-pencil-square"></i> Edit
  </button></td>
)}
                      
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty text="No passengers" />
        )}
      </DetailBlock>

      {passengers.length > 0 && (
        <DetailBlock icon="bi-receipt" title="Passenger-wise Fare" count={passengers.length} accent={ACCENTS.fare}>
          <div className="table-responsive" style={detailTableWrap}>
            <table className="table table-sm align-middle mb-0" style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  {["Passenger", "Type", "Base Fare", "Tax", "Airline Fees", "Other Charges", "Discount", "Net Fare", "Total Fare", "Status"].map((h) => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {passengers.map((p, i) => {
                  const type = String(p.pax_type || "").toLowerCase();
                  const name = [p.title, p.first_name, p.last_name].filter(Boolean).join(" ");
                  return (
                    <tr key={p.id ?? i}>
                      <td style={tdStyle} className="fw-semibold text-capitalize">{show(name)}</td>
                      <td style={tdStyle}><Pill value={titleCase(p.pax_type)} accent={PAX_ACCENTS[type] || ACCENTS.passengers} /></td>
                      <td style={tdStyle}>{money(p.base_fare)}</td>
                      <td style={tdStyle}>{money(p.tax_amount)}</td>
                      <td style={tdStyle}>{money(p.airline_fees)}</td>
                      <td style={tdStyle}>{money(p.other_charges)}</td>
                      <td style={tdStyle}>{money(p.discount)}</td>
                      <td style={tdStyle}>{money(p.net_fare)}</td>
                      <td style={{ ...tdStyle, color: "#047857", fontWeight: 700 }}>{money(p.total_fare)}</td>
                      <td style={tdStyle}>{p.status ? <Badge value={p.status} /> : "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </DetailBlock>
      )}

      <DetailBlock icon="bi-clock-history" title="Status Timeline" accent={ACCENTS.timeline}>
        <div className="row g-2">
          {steps.map((st, i) => {
            const done = !!st.at;
            const color = !done ? "#cbd5e1" : st.danger ? DETAIL_ACCENTS.red : DETAIL_ACCENTS.green;
            return (
              <div key={st.label} className="col-6 col-md-3">
                <div className="d-flex align-items-center gap-2">
                  <span
                    className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: 30, height: 30, borderRadius: "50%", fontSize: 14, background: done ? color + "1a" : "#f8fafc", color, border: `2px solid ${color}` }}
                  >
                    <i className={"bi " + st.icon}></i>
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div className="fw-semibold" style={{ fontSize: 13, color: done ? "#0f172a" : "#94a3b8" }}>{st.label}</div>
                    <div style={{ fontSize: 11.5, color: done ? "#64748b" : "#cbd5e1" }}>{done ? dateTimeText(st.at) : "Pending"}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {(isCancelled || !isEmpty(booking.remarks) || booking.modified_at) && (
          <div className="mt-2 pt-2" style={{ borderTop: "1px dashed #eef1f7", fontSize: 13, color: "#475569" }}>
            {isCancelled && !isEmpty(booking.cancellation_reason) && (
              <div><strong>Cancellation reason:</strong> {booking.cancellation_reason}</div>
            )}
            {!isEmpty(booking.remarks) && <div><strong>Remarks:</strong> {booking.remarks}</div>}
            <div style={{ color: "#94a3b8", fontSize: 12 }}>Modified {dateTimeText(booking.modified_at)}</div>
          </div>
        )}
      </DetailBlock>

      <Modal show={!!editPax} onHide={() => !savingPax && setEditPax(null)} centered size="xl" backdrop="static" scrollable>
        {editPax && (
          <form onSubmit={handleSavePassenger}>
            <Modal.Header closeButton>
              <Modal.Title style={{ fontSize: 17, fontWeight: 700 }}>Update Passenger Details</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: "14px 18px" }}>
              <div className="row g-2">
              <div className="col-6 col-md-3">
                <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Type <span className="text-danger">*</span></label>
                <select className="form-select form-select-sm" name="pax_type" value={editPax.pax_type} onChange={handlePaxChange} required>
                  <option value="adult">Adult</option>
                  <option value="child">Child</option>
                  <option value="infant">Infant</option>
                </select>
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Title <span className="text-danger">*</span></label>
                <select className="form-select form-select-sm" name="title" value={editPax.title} onChange={handleTitleChange} required>
                  <option value="">Select Title</option>
                  {TITLES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>First Name <span className="text-danger">*</span></label>
                <input className="form-control form-control-sm" name="first_name" value={editPax.first_name} onChange={handlePaxChange} required maxLength={50} />
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Last Name <span className="text-danger">*</span></label>
                <input className="form-control form-control-sm" name="last_name" value={editPax.last_name} onChange={handlePaxChange} required maxLength={50} />
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Gender <span className="text-danger">*</span></label>
                <select className="form-select form-select-sm" name="gender" value={editPax.gender} onChange={handlePaxChange} required>
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
              
              <div className="col-6 col-md-3">
                <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Date of Birth</label>
                <DatePicker
                  value={toDateObject(editPax.dob)}
                  onChange={(d) => setEditPax((cur) => ({ ...cur, dob: d ? d.format("YYYY-MM-DD") : "" }))}
                  format="YYYY-MM-DD"
                  placeholder="Select date"
                  onOpenPickNewDate={false}
                  editable={false}
                  maxDate={new DateObject()}
                  inputClass="form-control form-control-sm"
                  containerStyle={{ width: "100%" }}
                  style={{ width: "100%" }}
                />
              </div>
              
              <div className="col-6 col-md-3">
                <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Wheel Chair Required</label>
                <select className="form-select form-select-sm" name="wheelchair" value={editPax.wheelchair} onChange={handlePaxChange}>
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Nationality</label>
                <input className="form-control form-control-sm" name="nationality" value={editPax.nationality} onChange={handlePaxChange} maxLength={60} />
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>PNR</label>
                <input className="form-control form-control-sm" name="pnr" value={editPax.pnr} onChange={handlePaxChange} maxLength={20} readOnly />
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Ticket Number</label>
                <input className="form-control form-control-sm" name="ticket_number" value={editPax.ticket_number} onChange={handlePaxChange} maxLength={30} />
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Seat Number</label>
                <input className="form-control form-control-sm" name="seat_number" value={editPax.seat_number} onChange={handlePaxChange} maxLength={10} />
              </div>
              <div className="col-12 mt-3">
                <div className="d-flex align-items-center gap-2" style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "var(--btn-bg)", borderBottom: "1px solid #e2e8f0", paddingBottom: 6 }}>
                  <i className="bi bi-journal-text"></i> Passport / Travel Document
                </div>
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Passport Number</label>
                <input className="form-control form-control-sm" name="passport_number" value={editPax.passport_number} onChange={handlePaxChange} maxLength={20} />
              </div>              
              <div className="col-6 col-md-3">
                <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Passport Issue Date</label>
                <DatePicker
                  value={toDateObject(editPax.passport_issue_date)}
                  onChange={(d) => setEditPax((cur) => ({ ...cur, passport_issue_date: d ? d.format("YYYY-MM-DD") : "" }))}
                  format="YYYY-MM-DD"
                  placeholder="Select date"
                  onOpenPickNewDate={false}
                  editable={false}
                  maxDate={new DateObject()}
                  inputClass="form-control form-control-sm"
                  containerStyle={{ width: "100%" }}
                  style={{ width: "100%" }}
                />
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Passport Expiry</label>
                <DatePicker
                  value={toDateObject(editPax.passport_expiry)}
                  onChange={(d) => setEditPax((cur) => ({ ...cur, passport_expiry: d ? d.format("YYYY-MM-DD") : "" }))}
                  format="YYYY-MM-DD"
                  placeholder="Select date"
                  onOpenPickNewDate={false}
                  editable={false}
                  minDate={toDateObject(editPax.passport_issue_date) || new DateObject()}
                  inputClass="form-control form-control-sm"
                  containerStyle={{ width: "100%" }}
                  style={{ width: "100%" }}
                />
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Passport Issuing Country</label>
                <Select
                  inputId="edit-pax-country"
                  options={countryOptions}
                  value={countryOptions.find((o) => o.value === editPax.passport_issuing_country) || (editPax.passport_issuing_country ? { value: editPax.passport_issuing_country, label: editPax.passport_issuing_country } : null)}
                  onChange={(o) => setEditPax((cur) => ({ ...cur, passport_issuing_country: o?.value || "" }))}
                  placeholder="Select Country"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={countrySelectStyles}
                />
              </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button type="button" className="btn btn-sm px-3" disabled={savingPax} onClick={() => setEditPax(null)} style={{ border: "1px solid #dbe2ea", borderRadius: 8, background: "#fff", color: "#475569" }}>
                Cancel
              </button>
              <button type="submit" className="btn btn-sm px-4 text-white" disabled={savingPax} style={{ borderRadius: 8, background: "var(--btn-bg)", fontWeight: 600 }}>
                {savingPax ? "Saving..." : "Update Passenger"}
              </button>
            </Modal.Footer>
          </form>
        )}
      </Modal>
    </section>
  );
};

export default BookingDetails;
